import { useState } from 'react'
import { cn } from '../utils'
import { LogOut, Trash2, CheckCircle, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { OrgRole } from './types'

export type OrgListItem = {
  id: string
  name: string
  role: OrgRole | 'pending'
  /** Present only for pending invitation rows */
  invitationId?: string
}

export type OrgsListProps = {
  orgs: OrgListItem[]
  selectedOrgId: string | null
  onSelect: (orgId: string) => void
  onLeave: (orgId: string) => Promise<void>
  onDelete?: (orgId: string) => Promise<void>
  onAcceptInvitation?: (invitationId: string) => Promise<void>
  onDeclineInvitation?: (invitationId: string) => Promise<void>
  className?: string
}

const btnClass =
  'inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-50 disabled:pointer-events-none transition-colors'

const deleteBtnClass =
  'inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 hover:border-destructive/20 disabled:opacity-50 disabled:pointer-events-none transition-colors'

function TableColumns() {
  return (
    <colgroup>
      <col style={{ width: '55%' }} />
      <col style={{ width: '15%' }} />
      <col style={{ width: '30%' }} />
    </colgroup>
  )
}

export function OrgsList({
  orgs,
  selectedOrgId,
  onSelect,
  onLeave,
  onDelete,
  onAcceptInvitation,
  onDeclineInvitation,
  className,
}: OrgsListProps) {
  if (orgs.length === 0) {
    return (
      <motion.div
        className={cn(
          'rounded-lg border border-border bg-card p-8 text-center',
          className,
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <p className="text-sm text-muted-foreground">
          You're not a member of any organizations yet.
        </p>
      </motion.div>
    )
  }

  return (
    <div className={cn('rounded-lg border border-border overflow-hidden', className)}>
      {/* Header */}
      <table className="w-full table-fixed">
        <TableColumns />
        <thead>
          <tr className="bg-card">
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Organization
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Role
            </th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
      </table>

      {/* Rows */}
      <AnimatePresence initial={false}>
        {orgs.map((org) => (
          <OrgRow
            key={org.id}
            org={org}
            selected={selectedOrgId === org.id}
            onSelect={() => onSelect(org.id)}
            onLeave={onLeave}
            onDelete={onDelete}
            onAccept={onAcceptInvitation}
            onDecline={onDeclineInvitation}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

function OrgRow({
  org,
  selected,
  onSelect,
  onLeave,
  onDelete,
  onAccept,
  onDecline,
}: {
  org: OrgListItem
  selected: boolean
  onSelect: () => void
  onLeave: (orgId: string) => Promise<void>
  onDelete?: (orgId: string) => Promise<void>
  onAccept?: (invitationId: string) => Promise<void>
  onDecline?: (invitationId: string) => Promise<void>
}) {
  const [leaving, setLeaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [acting, setActing] = useState<'accept' | 'decline' | null>(null)

  const isPending = org.role === 'pending' && org.invitationId

  async function handleLeave(e: React.MouseEvent) {
    e.stopPropagation()
    setLeaving(true)
    try {
      await onLeave(org.id)
    } finally {
      setLeaving(false)
    }
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation()
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete(org.id)
    } finally {
      setDeleting(false)
    }
  }

  async function handleAccept(e: React.MouseEvent) {
    e.stopPropagation()
    if (!org.invitationId || !onAccept) return
    setActing('accept')
    try {
      await onAccept(org.invitationId)
    } finally {
      setActing(null)
    }
  }

  async function handleDecline(e: React.MouseEvent) {
    e.stopPropagation()
    if (!org.invitationId || !onDecline) return
    setActing('decline')
    try {
      await onDecline(org.invitationId)
    } finally {
      setActing(null)
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] as const }}
      style={{ overflow: 'hidden' }}
    >
      <table
        className={cn(
          'w-full table-fixed border-t border-border transition-colors',
          !isPending && 'cursor-pointer',
          selected ? 'bg-primary/[0.08]' : !isPending && 'hover:bg-card',
        )}
        onClick={!isPending ? onSelect : undefined}
      >
        <TableColumns />
        <tbody>
          <tr>
            {/* Name */}
            <td className="px-4 py-3">
              <span className="text-sm font-medium text-foreground truncate">
                {org.name}
              </span>
            </td>

            {/* Role */}
            <td className="px-4 py-3">
              {isPending ? (
                <span className="text-sm text-status-warning-foreground">Pending</span>
              ) : (
                <span className="text-sm text-muted-foreground capitalize">{org.role}</span>
              )}
            </td>

            {/* Actions */}
            <td className="px-4 py-3 text-right">
              <AnimatePresence mode="wait" initial={false}>
                {isPending && onAccept && onDecline ? (
                  <motion.div
                    key="pending"
                    className="inline-flex items-center gap-2 justify-end"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <button
                      onClick={handleAccept}
                      disabled={acting !== null}
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    >
                      <CheckCircle className="h-3 w-3" />
                      {acting === 'accept' ? 'Accepting...' : 'Accept'}
                    </button>
                    <button
                      onClick={handleDecline}
                      disabled={acting !== null}
                      className={btnClass}
                    >
                      <X className="h-3 w-3" />
                      {acting === 'decline' ? 'Declining...' : 'Decline'}
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="member"
                    className="inline-flex items-center gap-2 justify-end"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {org.role === 'owner' ? (
                      onDelete && (
                        <button
                          onClick={handleDelete}
                          disabled={deleting}
                          className={deleteBtnClass}
                        >
                          <Trash2 className="h-3 w-3" />
                          {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                      )
                    ) : (
                      <button
                        onClick={handleLeave}
                        disabled={leaving}
                        className={btnClass}
                      >
                        <LogOut className="h-3 w-3" />
                        {leaving ? 'Leaving...' : 'Leave'}
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </td>
          </tr>
        </tbody>
      </table>
    </motion.div>
  )
}
